import { env } from '@/constants';
import { useArtworksStore } from '@/stores/ArtworkStore/ArtworksStore';
import { useAuthenticationStore } from '@/stores/AuthenticationStore/AuthenticationStore';
import type { StoreOptions } from '@/stores/store.types';
import { handleHttpError } from '@/utils/errorHandlers';
import { enrichInventoryItem } from '@/utils/inventoryUtils';
import { backOfficeMessages } from '@/views/gallery/back-office-messages';
import { NeonAlertService } from '@arcual/lib-app-neon';
import {
  type ArcualInventoryItem,
  type ArcualItemCategory,
  ArcualItemType,
  type ArcualMasterConfig,
  type ChildrenConfig,
  type ChildrenFilters,
  getInventoryService,
  type HttpError,
  type InventorySearchFilters,
  type ResultList,
  type SalesAgreementInventorySearchFilters,
} from '@arcual/lib-app-platform';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';

interface InventoryItemOptions extends StoreOptions {
  populateDigitalDossier?: boolean;
}

const InventoryStoreName = 'InventoryStore';

export const useInventoryStore = defineStore(InventoryStoreName, () => {
  const items: Ref<Map<string, Ref<ArcualInventoryItem>>> = ref<Map<string, Ref<ArcualInventoryItem>>>(new Map());

  const _setItem = async (item: ArcualInventoryItem, enrichItem = true) => {
    if (enrichItem) await enrichInventoryItem(item);

    const itemRef = items.value.get(item.id);
    if (itemRef) {
      itemRef.value = {
        ...itemRef.value,
        ...item,
      };
    } else {
      items.value.set(item.id, ref(item));
    }
  };

  const invalidateItem = (id: string) => items.value.delete(id);

  const resultSetRequest = async (requestFunction: () => Promise<ResultList<ArcualInventoryItem>>) => {
    const resultList = await requestFunction().catch((error: HttpError) => {
      handleHttpError(error);
      return { total: 0, items: [] };
    });

    const ids = resultList.items.map((item) => item.id);
    const actionsList = await getInventoryService().getBulkActions(ids);

    for (const item of resultList.items) {
      item.actions = actionsList.find((action) => action.id === item.id);
      await _setItem(item);
    }

    return resultList;
  };

  const getItems = async (filters?: InventorySearchFilters): Promise<ResultList<ArcualInventoryItem>> =>
    resultSetRequest(() => getInventoryService().getItems(filters));

  const getChildren = async (masterId: string, filters?: ChildrenFilters): Promise<ResultList<ArcualInventoryItem>> =>
    resultSetRequest(() => getInventoryService().getChildren(masterId, filters));

  const getNextChildForSale = async (masterId: string): Promise<ArcualInventoryItem | undefined> => {
    // TODO: support richer filtering on the BE so we can request specific item types
    const children = await getChildren(masterId, { page: 1, pageSize: env.filters.PAGE_SIZE, includeSold: false });
    return (
      children.items.find((child) => child.itemType === ArcualItemType.EDITION) ||
      children.items.find((child) => child.itemType === ArcualItemType.ARTIST_PROOF)
    );
  };

  const getItem = async (id: string, options?: InventoryItemOptions): Promise<Ref<ArcualInventoryItem>> => {
    if (options?.invalidateCache) {
      items.value.delete(id);
    }

    let item = items.value.get(id);

    if (!item) {
      const isAuthenticated = useAuthenticationStore().isAuthenticated().value;
      const service = isAuthenticated ? getInventoryService().getItem(id) : getInventoryService().getItemForGuest(id);

      const newItem = await service.catch((error: HttpError) => {
        handleHttpError(error);
        return null;
      });

      if (newItem) {
        await _setItem(newItem, isAuthenticated);
        item = items.value.get(id);
      }
    }

    if (item) {
      // TODO: this is messy as it is not clear which items do or do not have a digital dossier
      // TODO: refactor to ALWAYS load the inventory items object graph when placing it in the cache
      if (options?.populateDigitalDossier && !item.value.digitalDossier) {
        item.value.digitalDossier = await useArtworksStore().loadDigitalDossier(item.value);
      }

      return item;
    }

    throw new Error(`Inventory item ${id} not found!`);
  };

  const getItemActions = async (itemId: string) =>
    getInventoryService()
      .getItemActions(itemId)
      .catch((error) => {
        handleHttpError(error);
        return {};
      });

  const getItemListings = async (itemId: string) =>
    getInventoryService()
      .getItemListings(itemId)
      .catch((error) => {
        handleHttpError(error);
        return [];
      });

  const addChildren = async (id: string, editionsConfig: Array<ChildrenConfig>): Promise<void> => {
    await getInventoryService()
      .addChildren(id, editionsConfig)
      .catch((error) => {
        handleHttpError(error);
        return Promise.reject(error);
      });
  };

  const createMaster = async (
    id: string,
    masterConfig: ArcualMasterConfig,
    editionsConfig?: Array<ChildrenConfig>,
  ): Promise<Ref<ArcualInventoryItem> | undefined> => {
    try {
      const master = await getInventoryService().createMaster(id, masterConfig);
      if (editionsConfig) {
        await addChildren(id, editionsConfig);
      }
      await _setItem(master);
      return items.value.get(master.id);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const updateItem = async (artworkId: string, payload: ArcualInventoryItem): Promise<ArcualInventoryItem> => {
    try {
      // TODO: remove type once category is supported
      const result: ArcualInventoryItem = await getInventoryService().updateItem(artworkId, {
        ...payload,
        type: payload.category,
      } as ArcualInventoryItem);

      await _setItem(payload);
      return result;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const createItem = async (payload: Omit<ArcualInventoryItem, 'id'>): Promise<ArcualInventoryItem> => {
    try {
      // TODO: remove type once category is supported
      const result = await getInventoryService().createItem({
        ...payload,
        type: payload.category,
      } as Omit<ArcualInventoryItem, 'id'>);
      await _setItem({ ...result, category: (result as unknown as Record<string, ArcualItemCategory>).type });
      return result;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const deleteItem = async (item: ArcualInventoryItem): Promise<void> => {
    try {
      await getInventoryService().deleteItem(item.id);
      items.value.delete(item.id);
      NeonAlertService.info({ title: backOfficeMessages.success, message: backOfficeMessages.artworkDeleted });
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const uploadItem = async (item: Omit<ArcualInventoryItem, 'id'>, useMediaService?: boolean): Promise<ArcualInventoryItem> => {
    try {
      const result = await getInventoryService().uploadItem(item, !!useMediaService);
      await _setItem(result);
      return result;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const getUnlistedItemsByActivation = async (
    activationId: string,
    filters?: InventorySearchFilters,
  ): Promise<ResultList<ArcualInventoryItem>> =>
    resultSetRequest(() => getInventoryService().getUnlistedItemsByActivation(activationId, filters));

  const getItemsAvailableForSale = async (
    filters?: SalesAgreementInventorySearchFilters,
  ): Promise<ResultList<ArcualInventoryItem>> =>
    resultSetRequest(() => getInventoryService().getItemsAvailableForSale(filters));

  const reset = () => {
    items.value = new Map();
  };

  return {
    items,
    invalidateItem,
    getItems,
    getChildren,
    getNextChildForSale,
    getItem,
    getItemActions,
    getItemListings,
    addChildren,
    createMaster,
    updateItem,
    createItem,
    deleteItem,
    uploadItem,
    getUnlistedItemsByActivation,
    getItemsAvailableForSale,
    reset,
  };
});
